import { useFormik } from "formik";
import * as yup from "yup";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import { FormHelperText } from "@mui/material";
import PhoneInput from "react-phone-input-2";
import BaseButton from "../../../components/ui/BaseButton";
import BaseModal from "../../../components/ui/BaseModal";
import BaseTextField from "../../../components/ui/BaseTextField";
import BaseTooltip from "../../../components/ui/BaseTooltip";
import { createCustomerPortalUser } from "../../../services/api/customerUsers";
import { showToast } from "../../../store/toast/toastSlice";

const validationSchema = yup.object({
  firstName: yup
    .string()
    .matches(/^[A-Za-z\s]+$/, "First name must only contain alphabets")
    .required("First name is required"),
  lastName: yup
    .string()
    .matches(/^[A-Za-z\s]+$/, "Last name must only contain alphabets")
    .required("Last name is required"),
  phoneNumber: yup.string().required("Phone number is required"),
  email: yup.string().email("Enter a valid email").required("Email is required"),
});

const INITIAL_VALUES = {
  firstName: "",
  lastName: "",
  phoneNumber: "",
  email: "",
};

// No password field: the API generates one and mails it to the new user, who is
// forced to change it on first login.
const CreateCustomerUserModal = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const { mutateAsync: createUserMutation, isPending: isSubmitting } = useMutation({
    mutationFn: createCustomerPortalUser,
  });

  const formik = useFormik({
    initialValues: INITIAL_VALUES,
    validationSchema,
    onSubmit: handleSubmit,
    validateOnChange: true,
    validateOnBlur: true,
  });

  async function handleSubmit(values) {
    try {
      await createUserMutation({
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        email: values.email.trim().toLowerCase(),
        phoneNumber: values.phoneNumber.toString(),
      });
      dispatch(
        showToast({
          message: "User created. Login details have been emailed to them.",
          type: "success",
        }),
      );
      queryClient.invalidateQueries({ queryKey: ["customerUsers"] });
      handleClose();
    } catch (error) {
      dispatch(
        showToast({
          message: error?.response?.data?.message || "Failed to create user",
          type: "error",
        }),
      );
    }
  }

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  return (
    <BaseModal
      open={open}
      close={handleClose}
      className="-top-[200px] overflow-auto"
      style={{ width: "1000px" }}
    >
      {/* The form lives INSIDE BaseModal on purpose. BaseModal renders through a
          MUI portal, so a <form> wrapped around it stays empty in the DOM and the
          fields end up outside any form — which left the browser free to treat
          them as part of the page behind the modal and autofill the email into
          the listing's search box. */}
      <form onSubmit={formik.handleSubmit} autoComplete="on">
        <div className="px-8 py-5">
          <h6 className="text-dark font-medium border-b pb-1">Add User</h6>
        </div>

        <div className="px-8 py-4 grid grid-cols-3 gap-x-6 gap-y-4">
          {/* First Name */}
          <BaseTextField
            height="60px"
            label={
              <span className="text-[#525354]">
                First Name <span className="text-red-500">*</span>
              </span>
            }
            InputLabelProps={{ shrink: true }}
            labelShrink={true}
            fullWidth
            id="firstName"
            name="firstName"
            autoComplete="given-name"
            placeholder="William"
            value={formik.values.firstName}
            onChange={(e) => {
              const value = e.target.value;
              if (/^[A-Za-z\s]*$/.test(value)) formik.handleChange(e);
            }}
            onBlur={formik.handleBlur}
            error={formik.touched.firstName && Boolean(formik.errors.firstName)}
            helperText={formik.touched.firstName && formik.errors.firstName}
          />

          {/* Last Name */}
          <BaseTextField
            height="60px"
            label={
              <span className="text-[#525354]">
                Last Name <span className="text-red-500">*</span>
              </span>
            }
            InputLabelProps={{ shrink: true }}
            labelShrink={true}
            fullWidth
            id="lastName"
            name="lastName"
            autoComplete="family-name"
            placeholder="Clark"
            value={formik.values.lastName}
            onChange={(e) => {
              const value = e.target.value;
              if (/^[A-Za-z\s]*$/.test(value)) formik.handleChange(e);
            }}
            onBlur={formik.handleBlur}
            error={formik.touched.lastName && Boolean(formik.errors.lastName)}
            helperText={formik.touched.lastName && formik.errors.lastName}
          />

          {/* Phone Number */}
          <div className="relative">
            <label
              className="absolute z-10 bg-white px-1"
              style={{
                top: "-6px",
                left: "12px",
                fontSize: "12px",
                fontFamily: "Poppins",
                color: "#686868",
                fontWeight: 400,
                lineHeight: 1,
              }}
            >
              Phone Number <span className="text-red-500">*</span>
            </label>
            <PhoneInput
              specialLabel=""
              inputClass={`
                !w-full !font-normal !bg-white !border !rounded-lg
                ${
                  formik.touched.phoneNumber && formik.errors.phoneNumber
                    ? "!border-red-600"
                    : "!border-gray-300 focus:!border-[#0F40AE]"
                }
                focus:!outline-none focus:!ring-1 focus:!ring-[#0F40AE]
              `}
              buttonClass="!border-none !bg-transparent"
              // Height is derived, not fixed. BaseTextField defaults to
              // fullHeight, which forces `height: 60% !important` on the inner
              // input and ignores any `height` prop — so a MUI field here is
              // 14px padding + one 13px line, ~47px. Matching that padding and
              // line-height keeps the phone box the same height as its
              // neighbours instead of pinning it to a number that drifts.
              inputStyle={{
                height: "auto",
                boxSizing: "border-box",
                padding: "14px 14px 14px 48px",
                lineHeight: 1.4375,
                width: "100%",
                borderRadius: "8px",
                fontSize: "13px",
                fontFamily: "Poppins",
                color: "#686868",
                fontWeight: 300,
                ...(formik.touched.phoneNumber &&
                  formik.errors.phoneNumber && { border: "1px solid #d32f2f" }),
              }}
              country="au"
              inputProps={{ name: "phoneNumber", id: "phoneNumber", autoComplete: "tel" }}
              value={formik.values.phoneNumber}
              onChange={(value) => formik.setFieldValue("phoneNumber", value)}
              onBlur={() => formik.setFieldTouched("phoneNumber", true)}
            />
            {formik.touched.phoneNumber && formik.errors.phoneNumber && (
              <FormHelperText error>{formik.errors.phoneNumber}</FormHelperText>
            )}
          </div>

          {/* Email */}
          <BaseTextField
            height="60px"
            label={
              <span className="text-[#525354]">
                Email <span className="text-red-500">*</span>
              </span>
            }
            InputLabelProps={{ shrink: true }}
            labelShrink={true}
            fullWidth
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            placeholder="user@example.com"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={formik.touched.email && formik.errors.email}
          />

          {/* Sits beside the email box, where the note used to be. The form has no
              password field — one is generated server-side and mailed to this
              address — so the explanation hangs off this icon. */}
          {/* -ml-4 pulls the icon back across most of the grid's 24px gap-x-6, so
              it reads as attached to the email box rather than floating mid-row. */}
          <div className="col-span-1 flex items-center self-start h-[50px] -ml-4">
            <BaseTooltip
              placement="top"
              content={
                <p className="text-white">
                  A password is generated automatically and emailed to the user.
                </p>
              }
            >
              <i className="fa-light fa-circle-info text-[#686868] text-base cursor-pointer" />
            </BaseTooltip>
          </div>
        </div>

        <div
          className="d-flex content-center bg-white py-6 mt-6"
          style={{ boxShadow: "0px 0px 14px 0px rgba(0, 0, 0, 0.07)" }}
        >
          <BaseButton
            className="px-[28px] py-[5px] text-primary hover:!text-white"
            variant="outlined"
            color="primary"
            type="button"
            style={{ marginRight: "16px" }}
            onClick={handleClose}
          >
            Cancel
          </BaseButton>
          {/* No onClick: the form above is a real form now, so type="submit"
                alone submits it. Keeping both would fire handleSubmit twice. */}
          <BaseButton
            disabled={Object.keys(formik.errors).length > 0}
            isLoading={isSubmitting}
            loaderSize="22px"
            color="primary"
            className="py-[5px] px-[20px] min-w-32"
            type="submit"
          >
            Create
          </BaseButton>
        </div>
      </form>
    </BaseModal>
  );
};

export default CreateCustomerUserModal;
